'use client'

import { LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image from 'next/image'
import { ReactNode } from 'react'

const AssessmentItems = [
    {
        title: 'Durability',
        illustration: '/illustrations/assessment/assessment-durability.png',
        description:
            'Ensures carbon credits yield enduring climate benefits, by minimising reversal risks and maintaining environmental integrity as permanently as possible.',
    },
    {
        title: 'Additionality',
        illustration: '/illustrations/assessment/assessment-additionality.png',
        description:
            'Credit where credit’s due. Ensuring funds drive new, impactful changes beyond what wouldn’t happen in a business-as-usual scenario.',
    },
    {
        title: 'Measurability',
        illustration: '/illustrations/assessment/assessment-measurability.png',
        description:
            'Quantifiable proof that your contributions are delivering the carbon impact promised.',
    },
    {
        title: 'Co-benefits',
        illustration: '/illustrations/assessment/assessment-cobenefits.png',
        description:
            'Climate projects are a great mechanism to improve biodiversity, protect endangered species, and support local communities through jobs and education, and more.',
    },
    {
        title: 'Risk mitigation',
        illustration: '/illustrations/assessment/assessment-risk-mitigation.png',
        description:
            'Every climate project has the risk of failed delivery. We preemptively address potential failures to ensure return on your climate contributions. ',
    },
    {
        title: 'Deliverability',
        illustration: '/illustrations/assessment/assessment-deliverability.png',
        description:
            'Developers are intrinsic to project success. We vet developer teams to ensure they can fulfil their project.',
    },
]

const AssessmentLabelsOnly = ({
    title = 'Deliver a positive global impact',
    introduction,
}: {
    title?: ReactNode
    introduction?: ReactNode
}) => {
    const { palette } = LuneTheme
    const { smScreen, mdScreen } = useDevice()

    return (
        <Box>
            <Text
                variant={mdScreen ? 'h5' : 'h4'}
                sx={{
                    mb: smScreen ? 4 : 6,
                    width: '100%',
                    textAlign: smScreen ? 'left' : 'center',
                }}
            >
                {title}
            </Text>
            <Text
                variant={mdScreen ? 'body2' : 'body1'}
                sx={{ mb: smScreen ? 4 : 6, textAlign: smScreen ? 'left' : 'center' }}
            >
                {introduction ?? (
                    <>
                        Our experts leverage a rigorous vetting process and comprehensive six-point
                        evaluation criteria {!mdScreen && <br />} to connect you with climate
                        projects that deliver measurable impact and peace of mind.
                    </>
                )}
            </Text>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: smScreen
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'repeat(3, minmax(0, 1fr))',
                    gap: 3,
                    pt: 6,
                    pb: 6,
                }}
            >
                {AssessmentItems.map((item) => (
                    <Box
                        key={item.title}
                        sx={{
                            backgroundColor: `${palette.White}`,
                            p: 1,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: '100%',
                            color: smScreen ? `${palette.Grey900}` : `${palette.Grey500}`,
                            transition: 'all 100ms ease-in-out',
                            boxShadow: 'none',
                            border: '1px solid',
                            borderColor: `${palette.Grey300}`,
                            img: {
                                opacity: smScreen ? 1 : 0.5,
                                filter: smScreen ? `grayscale(0%)` : `grayscale(100%)`,
                                willChange: 'filter, opacity',
                                transition: 'all 100ms ease-in-out',
                            },
                            '&:hover': {
                                borderColor: `${palette.White}`,
                                boxShadow: '0px 16px 24px 0px rgba(0, 0, 0, 0.15)',
                                color: `${palette.Grey900}`,
                                img: {
                                    opacity: 1,
                                    filter: 'grayscale(0%)',
                                },
                            },
                        }}
                    >
                        <Image src={item.illustration} width={64} height={64} alt={''} />
                        <Box sx={{ ml: '12px' }}>
                            <Text variant={'button'}>{item.title}</Text>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

const DescribedAssessment = () => {
    const { palette } = LuneTheme
    const { smScreen, mdScreen } = useDevice()

    return (
        <Box>
            <Text
                variant={mdScreen ? 'h5' : 'h4'}
                sx={{
                    mb: smScreen ? 4 : 6,
                    width: '100%',
                    textAlign: smScreen ? 'left' : 'center',
                }}
            >
                Deliver a positive global impact
            </Text>
            <Text
                variant={mdScreen ? 'body2' : 'body1'}
                sx={{ mb: smScreen ? 4 : 6, textAlign: smScreen ? 'left' : 'center' }}
            >
                Our experts leverage a rigorous vetting process and comprehensive six-point
                evaluation criteria {!mdScreen && <br />} to connect you with climate projects that
                deliver measurable impact and peace of mind.
            </Text>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    pt: 6,
                    pb: 6,
                    position: 'relative',
                    '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: '1px',
                        backgroundColor: `${palette.Grey300}`,
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        m: 'auto',
                        zIndex: -1,
                    },
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        transform: 'rotate(45deg)',
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: `${palette.Grey300}`,
                        bottom: '5.5px',
                        left: '-0.5px',
                        right: 0,
                        m: 'auto',
                    },
                }}
            >
                {AssessmentItems.map((item, index) => (
                    <Box
                        key={`assessment-item-${index}`}
                        sx={{
                            backgroundColor: `${palette.White}`,
                            p: 1,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            maxWidth: '554px',
                            color: smScreen ? `${palette.Grey900}` : `${palette.Grey500}`,
                            transition: 'all 100ms ease-in-out',
                            boxShadow: 'none',
                            border: '1px solid',
                            borderColor: `${palette.Grey300}`,
                            img: {
                                opacity: smScreen ? 1 : 0.5,
                                filter: smScreen ? `grayscale(0%)` : `grayscale(100%)`,
                                willChange: 'filter, opacity',
                                transition: 'all 100ms ease-in-out',
                            },
                            '&:hover': {
                                borderColor: `${palette.White}`,
                                boxShadow: '0px 16px 24px 0px rgba(0, 0, 0, 0.15)',
                                color: `${palette.Grey900}`,
                                img: {
                                    opacity: 1,
                                    filter: 'grayscale(0%)',
                                },
                            },
                        }}
                    >
                        <Image
                            src={item.illustration}
                            width={64}
                            height={64}
                            alt={`assessment-item-img-${index}`}
                        />
                        <Box
                            sx={{
                                ml: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1 / 2,
                            }}
                        >
                            <Text variant={'button'}>{item.title}</Text>
                            <Text variant={'caption'}>{item.description}</Text>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

const Assessment = ({
    hideDescriptions = false,
    title,
    introduction,
}: {
    hideDescriptions?: boolean
    title?: ReactNode
    introduction?: ReactNode
}) => {
    if (hideDescriptions) {
        return <AssessmentLabelsOnly title={title} introduction={introduction} />
    }
    return <DescribedAssessment />
}

export default Assessment
